import PropTypes from 'prop-types';
import makeStyles from '@mui/styles/makeStyles';
import { Link } from 'react-router-dom';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Avatar from '@mui/material/Avatar';
import AvatarIcon from '@mui/icons-material/AccountCircleOutlined';
import getImageLink from 'utils/getImageLink';
import getLink from 'utils/getLink';
import getLinkUser from 'utils/getLinkUser';
import formatDate from 'date-fns/format';
import type { I18nStories } from '../../../api/database/Models';

const getImage = (image: any, version: any) => {
  if (image && image.md5) return getImageLink(image.md5, version, image.format);
  return false;
};

const useStyles = makeStyles(
  {
    container: {
      // height: 380
    },
    imageLink: {
      '-webkit-border-top-right-radius': '4px',
      borderTopRightRadius: 4,
      '-webkit-border-top-left-radius': '4px',
      borderTopLeftRadius: 4,
      backgroundPosition: '50% 50%',
      borderBottom: '1px solid rgba(0,0,0,.0785)',
      backgroundOrigin: 'border-box',
      backgroundSize: 'cover',
      backgroundColor: '#f0f0f0',
      width: '100%',
      height: 200,
      display: 'block',
    },
    containerContent: {
      padding: 15,
      // height: 180
    },
    title: {
      paddingTop: 16,
      marginTop: 0,
      fontSize: 30,
      lineHeight: 1.04,
      fontWeight: 600,
      letterSpacing: '-.015em',
      color: 'rgba(0,0,0,.84)',
      fill: 'rgba(0,0,0,.84)',
    },
    subtitle: {
      '--x-height-multiplier': 0.342,
      '--baseline-multiplier': 0.22,
      color: 'rgba(0,0,0,.54)',
      fontFamily:
        'wm-content-sans-serif-font,"Lucida Grande","Lucida Sans Unicode","Lucida Sans",Geneva,Arial,sans-serif',
      fontSize: 24,
      lineHeight: 1.22,
      letterSpacing: '-.012em',
    },
    caption: {
      marginBottom: 7,
    },
    containerAvatar: {
      marginTop: 20,
      paddingBottom: 10,
      flex: '0 0 auto',
    },
    containerPostMeta: {
      paddingLeft: 10,
      fontSize: 16,
    },
    authorFooter: {
      '&:hover': {
        textDecoration: 'underline',
      },
    },
    dateLink: {
      '&:hover': {
        color: 'rgba(0,0,0,.68)',
      },
    },
  },
  { name: 'WMCardPostBig' }
);

const CardPostBig = ({
  post,
  publication: publicationTmp = false,
}: {
  post: I18nStories;
  publication: boolean;
}): React.ReactElement => {
  const classes = useStyles();
  const { featureImg, title, subtitle, readingTime, User, publishDate, Publication } = post;
  const author = User || {};
  const { username, name, avatar } = author;

  const authorName = name || username;

  const publication = Publication || publicationTmp;

  const minutes = readingTime ? readingTime / 60 / 1000 : 0;
  // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'string' is not assignable to par... Remove this comment to see the full error message
  const readingTimeMin = Math.ceil(minutes.toFixed(2));

  const image = featureImg && featureImg.md5 && getImage(featureImg, 'fit_c_800x240');
  const styleAImage = image
    ? {
        backgroundImage: `url("${image}")`,
      }
    : {};

  const link = getLink(post);
  const linkUser = getLinkUser(User);
  const publicationLink = publication && publication.slug ? `/${publication.slug}` : false;

  // @ts-expect-error ts-migrate(2769) FIXME: No overload matches this call.
  const publishDateDate = new Date(publishDate);

  return (
    <Paper elevation={2}>
      <Grid container className={classes.container}>
        <Grid item xs={12}>
          <Link aria-label={title} to={link}>
            <div className={classes.imageLink} style={styleAImage} />
          </Link>
        </Grid>
        <Grid item xs={12}>
          <Grid container className={classes.containerContent} alignContent="space-between">
            <Grid item xs={12}>
              <Link to={link}>
                <Typography className={classes.title} variant="h3">
                  {title}
                </Typography>
              </Link>
              <Link to={link}>
                <Typography className={classes.subtitle} variant="h4">
                  {subtitle}
                </Typography>
              </Link>
            </Grid>

            <Grid item xs={12} className={classes.containerAvatar}>
              <Grid container>
                <Grid item xs="auto">
                  <Link to={linkUser}>
                    {avatar && avatar.md5 ? (
                      // @ts-expect-error ts-migrate(2769) FIXME: No overload matches this call.
                      <Avatar alt={authorName} src={getImage(avatar, 'fit_c_72x72')} />
                    ) : (
                      <Avatar alt={authorName}>
                        <AvatarIcon />
                      </Avatar>
                    )}
                  </Link>
                </Grid>
                <Grid item xs="auto" className={classes.containerPostMeta}>
                  <Link className={classes.authorFooter} to={linkUser}>
                    {authorName}
                  </Link>
                  {publication.name && (
                    <>
                      {' '}
                      in{' '}
                      {/* @ts-expect-error ts-migrate(2322) FIXME: Type 'string | false' is not assignable to type 'T... Remove this comment to see the full error message */}
                      <Link className={classes.authorFooter} to={publicationLink}>
                        {publication.name}
                      </Link>
                    </>
                  )}
                  <Typography component="div" variant="caption">
                    <Link className={classes.dateLink} to={link}>
                      {/* @ts-expect-error ts-migrate(2322) FIXME: Type 'Date | undefined' is not assignable to type ... Remove this comment to see the full error message */}
                      <time dateTime={publishDate}>
                        {formatDate(publishDateDate, 'MMM d, yyyy')}
                      </time>
                    </Link>{' '}
                    · {readingTimeMin} min read
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Paper>
  );
};

CardPostBig.propTypes = {
  post: PropTypes.objectOf(PropTypes.any).isRequired,
  publication: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
};

export default CardPostBig;
